export interface Crumb {
  label: string;
  /** Absent on the current page. */
  to?: string;
  /** The group on the `to` tab to open and scroll to. */
  group?: string;
}

const TABS = {
  procedures: "Procedures",
  exams: "Exams",
  questionnaires: "Questionnaires",
  "performance-measures": "Performance measures",
  devices: "Devices",
  locations: "Locations",
} as const;

type Tab = keyof typeof TABS;

/**
 * Several kinds have no tab of their own: they are a group on another kind's
 * tab, and `/catalog/<kind>` is not a page. Their crumbs name that tab, then
 * the group, which links to the tab scrolled to it, so every crumb leads
 * somewhere. A kind's path segment is its group's id on the tab.
 */
const KINDS: Record<string, { tab: Tab; noun: string }> = {
  procedures: { tab: "procedures", noun: "procedure" },
  protocols: { tab: "procedures", noun: "protocol" },
  diagnostics: { tab: "procedures", noun: "diagnostic" },
  treatments: { tab: "procedures", noun: "treatment" },
  exams: { tab: "exams", noun: "exam" },
  findings: { tab: "exams", noun: "finding" },
  "performance-measures": { tab: "performance-measures", noun: "performance measure" },
  questionnaires: { tab: "questionnaires", noun: "questionnaire" },
  flows: { tab: "questionnaires", noun: "flow" },
  devices: { tab: "devices", noun: "device" },
  locations: { tab: "locations", noun: "location" },
};

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

/** Catalog > tab > group > item > Edit, for any `/catalog/<kind>/<id>[/edit]` path. */
export function catalogCrumbs(pathnames: readonly string[], itemTitle: string | null): Crumb[] {
  const [, kind, id, action] = pathnames;
  const known = kind ? KINDS[kind] : undefined;
  const crumbs: Crumb[] = [{ label: "Catalog", to: "/catalog" }];
  if (!kind || !known) return kind ? [...crumbs, { label: capitalize(kind) }] : [{ label: "Catalog" }];

  const tab = `/catalog/${known.tab}`;
  crumbs.push({ label: TABS[known.tab], to: tab });
  if (id && kind !== known.tab) crumbs.push({ label: capitalize(kind), to: tab, group: kind });
  if (id === "new") crumbs.push({ label: `New ${known.noun}` });
  else if (id) crumbs.push({ label: itemTitle ?? capitalize(known.noun), to: `/catalog/${kind}/${id}` });
  if (id && action) crumbs.push({ label: capitalize(action) });

  return crumbs.map((crumb, index) => (index === crumbs.length - 1 ? { label: crumb.label } : crumb));
}
